import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight, ArrowLeft, Sparkles, CheckCircle2, AlertTriangle,
  Info, ShieldCheck, Heart, Compass,
} from "lucide-react";

/* Parent Preparedness Check — client-side scoring rubric.
   10 questions, each 1-5. Sum → 10-50. Percentage → tier + guidance. */

const QUESTIONS = [
  { key: "time",       label: "How many hours a day can you realistically spend guiding your child's learning?",
    options: [{ v: 1, t: "Less than 30 min" }, { v: 2, t: "30 min – 1 hr" }, { v: 3, t: "1 – 2 hrs" }, { v: 4, t: "2 – 4 hrs" }, { v: 5, t: "4+ hrs" }] },
  { key: "patience",   label: "How would you rate your patience during a hard learning moment?",
    options: [{ v: 1, t: "Very short-fused" }, { v: 2, t: "Sometimes struggle" }, { v: 3, t: "Balanced" }, { v: 4, t: "Usually patient" }, { v: 5, t: "Very patient" }] },
  { key: "philosophy", label: "How clearly do you know what kind of learner your child is?",
    options: [{ v: 1, t: "No idea yet" }, { v: 2, t: "Some hunches" }, { v: 3, t: "A few strong hints" }, { v: 4, t: "Fairly clear picture" }, { v: 5, t: "I know deeply" }] },
  { key: "finances",   label: "How prepared is your family financially for the learning journey?",
    options: [{ v: 1, t: "Tight" }, { v: 2, t: "Manageable with effort" }, { v: 3, t: "Comfortable" }, { v: 4, t: "Well-planned" }, { v: 5, t: "Very well-planned" }] },
  { key: "emotional",  label: "How is your own emotional bandwidth right now?",
    options: [{ v: 1, t: "Very stretched" }, { v: 2, t: "Often tired" }, { v: 3, t: "Okay-ish" }, { v: 4, t: "Mostly steady" }, { v: 5, t: "Rested & steady" }] },
  { key: "support",    label: "How much support do you have from your partner or family?",
    options: [{ v: 1, t: "None" }, { v: 2, t: "Very little" }, { v: 3, t: "Some" }, { v: 4, t: "A lot" }, { v: 5, t: "Full team" }] },
  { key: "own_learning", label: "How comfortable are you learning new subjects yourself as needed?",
    options: [{ v: 1, t: "Very uncomfortable" }, { v: 2, t: "Reluctant" }, { v: 3, t: "Neutral" }, { v: 4, t: "Willing" }, { v: 5, t: "I love it" }] },
  { key: "community",  label: "How connected are you (or willing to be) to homeschool / parenting communities?",
    options: [{ v: 1, t: "Not at all" }, { v: 2, t: "A little" }, { v: 3, t: "Moderately" }, { v: 4, t: "Actively" }, { v: 5, t: "Very connected" }] },
  { key: "structure",  label: "How well do you handle building and following a weekly schedule?",
    options: [{ v: 1, t: "Poorly" }, { v: 2, t: "With effort" }, { v: 3, t: "Manage" }, { v: 4, t: "Good at it" }, { v: 5, t: "Excellent" }] },
  { key: "openness",   label: "How open are you to changing course if something isn't working?",
    options: [{ v: 1, t: "Stubborn" }, { v: 2, t: "Slow to change" }, { v: 3, t: "Neutral" }, { v: 4, t: "Adaptable" }, { v: 5, t: "Very adaptable" }] },
];

function tier(score) {
  const pct = Math.round((score / (QUESTIONS.length * 5)) * 100);
  if (pct >= 80) return { pct, level: "High Readiness", color: "green",
    headline: "You're wonderfully prepared to guide this journey.",
    body: "Your combination of time, patience, structure and openness is exactly what homeschooling or hybrid learning needs. Trust yourself — and remember to build in rest.",
    actions: ["Take the child assessment next", "Pick a curriculum from the Explorer", "Join a community to stay energised", "Plan a weekly review with your partner"],
  };
  if (pct >= 60) return { pct, level: "Good Readiness", color: "blue",
    headline: "You're ready — with one or two areas to strengthen.",
    body: "You have the essentials to lead your child's learning. Identify the 1-2 lowest scores above and set a plan to build them (community help, better routines, or more time).",
    actions: ["Take the child assessment", "Find a community for weekly support", "Consider a part-time tutor for weaker subjects", "Try the Activities page for age-based routines"],
  };
  if (pct >= 40) return { pct, level: "Building Readiness", color: "orange",
    headline: "Hybrid learning may be your sweet spot right now.",
    body: "Full-time homeschooling would be demanding at this stage. A hybrid model (school + home + tutors + communities) can give your child the best of both — and lower the load on you.",
    actions: ["Explore Hybrid / Microschool curricula", "Try short daily windows (30 min) before scaling up", "Book a mentor session for planning", "Rebuild your own energy first — take a week off before deciding"],
  };
  return { pct, level: "Prep First", color: "red",
    headline: "You care deeply — but the timing may need work.",
    body: "Homeschooling asks a lot. Right now, focus on rebuilding your bandwidth, finding community support, and getting clarity on what your child needs. A good school + strong home enrichment can be a beautiful path too.",
    actions: ["Read our Homeschool Knowledge Center", "Chat with the AI Assistant about your worries", "Join a community for peer support", "Consider traditional school + rich after-school learning for now"],
  };
}

export default function ParentReadiness() {
  const [step, setStep] = useState(-1);   // -1 = intro
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const cur = step >= 0 && step < QUESTIONS.length ? QUESTIONS[step] : null;
  const progress = step < 0 ? 0 : Math.round(((step + 1) / QUESTIONS.length) * 100);

  const answer = (val) => {
    setAnswers({ ...answers, [cur.key]: val });
    setTimeout(() => {
      if (step >= QUESTIONS.length - 1) setDone(true);
      else setStep(step + 1);
    }, 200);
  };

  const total = Object.values(answers).reduce((a, b) => a + b, 0);
  const result = done ? tier(total) : null;

  if (done && result) {
    const colorMap = {
      green: "from-brand-green to-emerald-700",
      blue: "from-brand-blue to-brand-blue-hover",
      orange: "from-brand-orange to-orange-600",
      red: "from-red-500 to-red-700",
    };
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className={`rounded-3xl p-10 text-white bg-gradient-to-br ${colorMap[result.color]} text-center`}>
          <Sparkles size={32} className="mx-auto mb-3" />
          <div className="text-sm uppercase tracking-widest opacity-80 mb-1">Your parent readiness</div>
          <div className="font-heading text-6xl font-bold">{result.pct}%</div>
          <div className="mt-1 text-lg font-heading">{result.level}</div>
        </div>
        <div className="surface p-8 mt-6">
          <h2 className="text-2xl font-semibold text-brand-blue">{result.headline}</h2>
          <p className="mt-3 text-brand-muted leading-relaxed">{result.body}</p>
          <div className="mt-6">
            <div className="overline mb-2">Suggested next steps</div>
            <ul className="space-y-2">
              {result.actions.map((a, i) => (
                <li key={i} className="flex gap-3"><CheckCircle2 size={18} className="text-brand-green shrink-0 mt-0.5" /><span className="text-brand-blue">{a}</span></li>
              ))}
            </ul>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/assessment" className="btn-primary">Take child assessment <ArrowRight size={14} /></Link>
            <Link to="/assistant" className="btn-outline">Chat with AI Assistant</Link>
            <button onClick={() => { setDone(false); setAnswers({}); setStep(-1); }} className="btn-ghost">Retake</button>
          </div>
        </div>
        <div className="mt-8 text-xs text-brand-muted flex gap-2 items-start">
          <Info size={14} className="shrink-0 mt-0.5" />
          <p>
            <strong>Not professional advice.</strong> This checkpoint is an educational self-reflection tool.
            Your circumstances change often — retake it any time. For serious concerns, please consult a qualified
            counsellor or educator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 min-h-[80vh]">
      {step >= 0 && (
        <div className="mb-10">
          <div className="flex justify-between text-xs text-brand-muted mb-2">
            <span>Question {step + 1} of {QUESTIONS.length}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-2 bg-brand-divider rounded-full overflow-hidden">
            <motion.div className="h-full bg-brand-green" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === -1 && (
          <motion.div key="intro" initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0}}
            className="text-center py-10">
            <div className="w-16 h-16 rounded-3xl bg-brand-orange-soft text-brand-orange mx-auto mb-5 flex items-center justify-center"><Compass size={30} /></div>
            <div className="overline mb-2">Parent Preparedness Checkpoint</div>
            <h1 data-testid="readiness-heading" className="text-4xl sm:text-5xl font-semibold text-brand-blue leading-tight">
              Are <span className="text-brand-green">you</span> ready to guide the journey?
            </h1>
            <p className="mt-4 text-lg text-brand-muted max-w-xl mx-auto">
              Ten warm questions about you — your time, patience, support system, and mindset.
              You'll get a readiness score and honest, practical next steps.
            </p>
            <button data-testid="readiness-begin-btn" onClick={() => setStep(0)} className="btn-primary mt-8">
              Begin check <ArrowRight size={16} />
            </button>
            <p className="mt-6 text-xs text-brand-muted max-w-md mx-auto">
              This is a self-reflection tool. There are no wrong answers and no data leaves your browser unless you sign up.
            </p>
          </motion.div>
        )}

        {cur && (
          <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <h2 className="text-3xl font-semibold text-brand-blue">{cur.label}</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {cur.options.map((o) => {
                const active = answers[cur.key] === o.v;
                return (
                  <button key={o.v}
                    data-testid={`readiness-${cur.key}-${o.v}`}
                    onClick={() => answer(o.v)}
                    className={`text-left px-5 py-4 rounded-2xl border-2 transition-all ${
                      active
                        ? "border-brand-green bg-brand-green-soft text-brand-blue"
                        : "border-brand-border bg-white hover:border-brand-blue text-brand-blue"
                    }`}>
                    {o.t}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {step > 0 && !done && (
        <div className="mt-10">
          <button onClick={() => setStep(step - 1)} className="btn-ghost">
            <ArrowLeft size={16} /> Back
          </button>
        </div>
      )}
    </div>
  );
}
